import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  HStack,
  Icon,
  Menu,
  Portal,
  Spinner,
  Table,
  Text,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import type { NavigateFunction } from 'react-router'
import { Link as RouterLink } from 'react-router'

import {
  Checkbox,
  ConfirmDialog,
  SortableColumnHeader,
} from '@mangovoice/mango-ui'
import type { ToastLike } from '@mangovoice/mango-ui'

import { DevicesApiProvider } from '../api/context'
import type { DevicesApi } from '../api/devicesApi'
import {
  DeviceCloneDialog,
  DeviceDeleteDialog,
  DeviceMassEditDialog,
  DeviceSetDesiredFirmwareDialog,
  DevicesListFiltersSideSheet,
  DevicesListToolbar,
  DevicesSearchBar,
} from '../components'
import { DevicesI18nProvider, useDevicesI18n } from '../i18n'
import type { DevicesTranslationLike } from '../i18n'
import type { DeviceSortColumn } from '../types'
import { formatApiError } from '../utils/formatApiError'
import { DeviceRow, EmptyState, ErrorState, LoadingState } from './DeviceRow'
import { useDevicesListState } from './useDevicesListState'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DevicesListPageProps {
  api: DevicesApi
  pbxId: number
  navigate: NavigateFunction
  toast: ToastLike
  actionsEnabled: boolean
  isStaff: boolean
  /**
   * Gates the "Add device" button independently of `actionsEnabled`.
   * Defaults to `actionsEnabled` when not provided.
   */
  canCreate?: boolean
  getEditRoute: (deviceId: number) => string
  addRoute: string
  wrapper?: React.ComponentType<{ children: React.ReactNode }>
  tableLayout?: 'fixed' | 'auto'
  truncateCells?: boolean
  addButtonColorPalette?: string
  /** Optional translation function for i18n; when omitted, components use fallback English */
  t?: DevicesTranslationLike
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const DevicesListPage = (props: DevicesListPageProps) => {
  return (
    <DevicesApiProvider api={props.api}>
      <DevicesI18nProvider t={props.t}>
        <DevicesListPageInner {...props} />
      </DevicesI18nProvider>
    </DevicesApiProvider>
  )
}

const DevicesListPageInner = ({
  pbxId,
  navigate,
  toast,
  actionsEnabled,
  isStaff,
  canCreate = actionsEnabled,
  getEditRoute,
  addRoute,
  wrapper: Wrapper,
  tableLayout = 'auto',
  truncateCells = false,
  addButtonColorPalette = 'brand',
}: DevicesListPageProps) => {
  const { text } = useDevicesI18n()
  const state = useDevicesListState({ pbxId, navigate, toast, getEditRoute })
  const handleSortChange = (field: string, direction: 'asc' | 'desc') => {
    state.handleSort(field as DeviceSortColumn, direction)
  }
  const [draftFilters, setDraftFilters] = useState(state.filters)
  const [rebuildAllConfirmOpen, setRebuildAllConfirmOpen] = useState(false)
  const [rebuildAndRebootConfirmOpen, setRebuildAndRebootConfirmOpen] =
    useState(false)

  useEffect(() => {
    // Keep draft in sync with applied filters when opening,
    // and if applied filters change externally while open.
    if (state.filtersVisible) {
      setDraftFilters(state.filters)
    }
  }, [state.filtersVisible, state.filters])

  if (!state.hasValidPbxId) {
    const errorContent = (
      <ErrorState
        message={text(
          'devices.list.pbxRequired',
          'PBX is required for the Devices page.',
        )}
      />
    )
    return Wrapper ? <Wrapper>{errorContent}</Wrapper> : errorContent
  }

  const contentState = (): 'error' | 'loading' | 'empty' | 'data' => {
    if (state.error) return 'error'
    if (state.isLoading) return 'loading'
    if (state.devices.length === 0) return 'empty'
    return 'data'
  }

  const cs = contentState()

  const content = (
    <Box
      h="90vh"
      bg="bg.default"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <Card.Root
        flex={1}
        display="flex"
        flexDirection="column"
        minH={0}
        minW={0}
        border="none"
      >
        <Card.Header p={6} flexShrink={0}>
          <Flex direction="column" gap={4}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={3}>
              <Heading size="lg">
                {text('devices.list.heading', 'Devices')}
              </Heading>
              <HStack gap={2} flexWrap="wrap" justify="flex-end">
                {(cs === 'data' || cs === 'loading' || cs === 'empty') && (
                  <DevicesSearchBar
                    value={state.searchTerm}
                    onChange={(value) => {
                      state.setSearchTerm(value)
                      state.setCurrentPage(1)
                    }}
                    placeholder={text(
                      'devices.list.searchPlaceholder',
                      'Search by name, user, extension, or MAC...',
                    )}
                    showFilterButton
                    filtersActive={state.filtersVisible}
                    activeFilterCount={state.activeFilterCount}
                    onToggleFilters={() =>
                      state.setFiltersVisible((prev) => !prev)
                    }
                  />
                )}
                {actionsEnabled &&
                  cs === 'data' &&
                  state.devices.length > 0 && (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={state.isAnyMutationPending}
                        >
                          {state.isAnyMutationPending && (
                            <Spinner size="xs" mr={2} />
                          )}
                          {state.selectedIds.size > 0
                            ? text(
                                'devices.list.actionsWithCount',
                                'Actions ({{count}} selected)',
                                { count: state.selectedIds.size },
                              )
                            : text('devices.list.actionsButton', 'Actions')}
                          <Icon boxSize={4} ml={1}>
                            <svg
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="m6 9 6 6 6-6" />
                            </svg>
                          </Icon>
                        </Button>
                      </Menu.Trigger>
                      <Portal>
                        <Menu.Positioner>
                          <Menu.Content minW="200px">
                            {state.selectedIds.size > 0 && (
                              <>
                                <Menu.Item
                                  value="mass-edit"
                                  onClick={() => state.setMassEditOpen(true)}
                                  px={4}
                                  py={2}
                                >
                                  {text(
                                    'devices.list.massEditAction',
                                    'Mass Edit ({{count}})',
                                    { count: state.selectedIds.size },
                                  )}
                                </Menu.Item>
                                {isStaff && (
                                  <>
                                    <Menu.Item
                                      value="reload-config"
                                      onClick={state.handleReloadConfiguration}
                                      px={4}
                                      py={2}
                                      disabled={state.isAnyMutationPending}
                                    >
                                      {state.isReloadingConfig && (
                                        <Spinner size="xs" mr={2} />
                                      )}
                                      {text(
                                        'devices.list.reloadConfigAction',
                                        'Reload configuration ({{count}})',
                                        { count: state.selectedIds.size },
                                      )}
                                    </Menu.Item>
                                    <Menu.Item
                                      value="reload-config-reboot"
                                      onClick={
                                        state.handleReloadConfigurationAndReboot
                                      }
                                      px={4}
                                      py={2}
                                      disabled={state.isAnyMutationPending}
                                    >
                                      {state.isReloadingConfigAndRebooting && (
                                        <Spinner size="xs" mr={2} />
                                      )}
                                      {text(
                                        'devices.list.reloadConfigRebootAction',
                                        'Reload config & reboot ({{count}})',
                                        { count: state.selectedIds.size },
                                      )}
                                    </Menu.Item>
                                    <Menu.Item
                                      value="set-firmware"
                                      onClick={() =>
                                        state.setSetFirmwareDialogOpen(true)
                                      }
                                      px={4}
                                      py={2}
                                      disabled={state.isAnyMutationPending}
                                    >
                                      {state.isSettingFirmware && (
                                        <Spinner size="xs" mr={2} />
                                      )}
                                      {text(
                                        'devices.list.setFirmwareAction',
                                        'Set desired firmware ({{count}})',
                                        { count: state.selectedIds.size },
                                      )}
                                    </Menu.Item>
                                  </>
                                )}
                              </>
                            )}
                            {state.selectedIds.size === 0 && isStaff && (
                              <>
                                <Menu.Item
                                  value="rebuild-all"
                                  onClick={() => setRebuildAllConfirmOpen(true)}
                                  px={4}
                                  py={2}
                                  disabled={state.isAnyMutationPending}
                                >
                                  {state.isRebuilding && (
                                    <Spinner size="xs" mr={2} />
                                  )}
                                  {text(
                                    'devices.list.rebuildAll',
                                    'Rebuild All',
                                  )}
                                </Menu.Item>
                                <Menu.Item
                                  value="rebuild-reboot"
                                  onClick={() =>
                                    setRebuildAndRebootConfirmOpen(true)
                                  }
                                  px={4}
                                  py={2}
                                  disabled={state.isAnyMutationPending}
                                >
                                  {state.isRebuildingAndRebooting && (
                                    <Spinner size="xs" mr={2} />
                                  )}
                                  {text(
                                    'devices.list.rebuildAndReboot',
                                    'Rebuild & Reboot',
                                  )}
                                </Menu.Item>
                              </>
                            )}
                          </Menu.Content>
                        </Menu.Positioner>
                      </Portal>
                    </Menu.Root>
                  )}
                {canCreate && (cs === 'data' || cs === 'empty') && (
                  <Button
                    asChild
                    colorPalette={addButtonColorPalette}
                    color="brand.contrast"
                    size="sm"
                  >
                    <RouterLink
                      to={addRoute}
                      aria-label={text('devices.list.addDevice', 'Add device')}
                    >
                      {text('devices.list.addDevice', 'Add device')}
                    </RouterLink>
                  </Button>
                )}
              </HStack>
            </Flex>
          </Flex>
        </Card.Header>

        <Card.Body
          px={6}
          py={3}
          flex={1}
          minH={0}
          display="flex"
          flexDirection="column"
        >
          <Box
            flex={1}
            minH={0}
            overflowY="auto"
            bg={cs !== 'data' ? 'bg.subtle' : undefined}
            borderRadius={cs !== 'data' ? 'lg' : undefined}
          >
            {cs === 'error' && (
              <ErrorState
                message={formatApiError(
                  state.error ??
                    text('devices.list.failedToLoad', 'Failed to load devices'),
                )}
              />
            )}
            {cs === 'loading' && <LoadingState />}
            {cs === 'empty' && <EmptyState />}
            {cs === 'data' && (
              <Box overflowX="auto" minW={0}>
                <Table.Root
                  variant="outline"
                  size="sm"
                  {...(tableLayout === 'fixed'
                    ? { tableLayout: 'fixed', width: '100%' }
                    : {})}
                >
                  <Table.Header>
                    <Table.Row>
                      {actionsEnabled ? (
                        <Table.ColumnHeader px={4} py={3} width="40px">
                          <Checkbox
                            checked={
                              state.devices.length > 0 &&
                              state.devices.every((d) =>
                                state.selectedIds.has(d.id),
                              )
                            }
                            onCheckedChange={(details) => {
                              if (details.checked) {
                                state.setSelectedIds(
                                  new Set(state.devices.map((d) => d.id)),
                                )
                              } else {
                                state.setSelectedIds(new Set())
                              }
                            }}
                            aria-label={text(
                              'devices.list.selectAllDevices',
                              'Select all devices',
                            )}
                          />
                        </Table.ColumnHeader>
                      ) : null}
                      <SortableColumnHeader
                        label={text('devices.list.columnName', 'Name')}
                        field="station_label"
                        sortField={state.sortState.column}
                        direction={state.sortState.direction}
                        onSortChange={handleSortChange}
                      />
                      <SortableColumnHeader
                        label={text(
                          'devices.list.columnPrimaryExt',
                          'Primary ext',
                        )}
                        field="primary_extension"
                        sortField={state.sortState.column}
                        direction={state.sortState.direction}
                        onSortChange={handleSortChange}
                      />
                      <SortableColumnHeader
                        label={text('devices.list.columnUser', 'User')}
                        field="assigned_user"
                        sortField={state.sortState.column}
                        direction={state.sortState.direction}
                        onSortChange={handleSortChange}
                      />
                      <SortableColumnHeader
                        label={text('devices.list.columnModel', 'Model')}
                        field="device_model"
                        sortField={state.sortState.column}
                        direction={state.sortState.direction}
                        onSortChange={handleSortChange}
                      />
                      <SortableColumnHeader
                        label={text(
                          'devices.list.columnMacAddress',
                          'MAC address',
                        )}
                        field="device_mac_address"
                        sortField={state.sortState.column}
                        direction={state.sortState.direction}
                        onSortChange={handleSortChange}
                      />
                      <Table.ColumnHeader px={4} py={3}>
                        {text('devices.list.columnFirmware', 'Firmware')}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader px={4} py={3}>
                        {text('devices.list.columnWarranty', 'Warranty')}
                      </Table.ColumnHeader>
                      <SortableColumnHeader
                        label={text(
                          'devices.list.columnLastCheckIn',
                          'Last check in',
                        )}
                        field="last_provisioned_time"
                        sortField={state.sortState.column}
                        direction={state.sortState.direction}
                        onSortChange={handleSortChange}
                      />
                      <SortableColumnHeader
                        label={text(
                          'devices.list.columnInitializedOn',
                          'Initialized on',
                        )}
                        field="created_at"
                        sortField={state.sortState.column}
                        direction={state.sortState.direction}
                        onSortChange={handleSortChange}
                      />
                      <Table.ColumnHeader px={4} py={3}>
                        {text(
                          'devices.list.columnConfiguration',
                          'Configuration',
                        )}
                      </Table.ColumnHeader>
                      <Table.ColumnHeader px={4} py={3} width="85px">
                        {text('devices.list.columnActions', 'Actions')}
                      </Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {state.devices.length > 0 ? (
                      state.devices.map((device) => (
                        <DeviceRow
                          key={device.id}
                          device={device}
                          actionsEnabled={actionsEnabled}
                          isStaff={isStaff}
                          isSelected={state.selectedIds.has(device.id)}
                          onToggleSelect={(d) => {
                            state.setSelectedIds((prev) => {
                              const next = new Set(prev)
                              if (next.has(d.id)) next.delete(d.id)
                              else next.add(d.id)
                              return next
                            })
                          }}
                          onDeleteClick={state.setDeviceToDelete}
                          onCloneClick={state.setDeviceToClone}
                          getEditRoute={getEditRoute}
                          truncate={truncateCells}
                        />
                      ))
                    ) : (
                      <Table.Row>
                        <Table.Cell
                          colSpan={actionsEnabled ? 12 : 11}
                          textAlign="center"
                          py={8}
                        >
                          <Text color="fg.muted">
                            {text(
                              'devices.list.noMatchingDevices',
                              'No devices match your search criteria',
                            )}
                          </Text>
                        </Table.Cell>
                      </Table.Row>
                    )}
                  </Table.Body>
                </Table.Root>
                <DevicesListToolbar
                  pageSize={state.pageSize}
                  onPageSizeChange={(size) => {
                    state.setPageSize(size)
                    state.setCurrentPage(1)
                  }}
                  currentPage={state.currentPage}
                  totalCount={state.totalCount}
                  onPageChange={state.setCurrentPage}
                  isLoading={state.isLoading}
                  showToolbar={state.totalCount > 0}
                />
              </Box>
            )}
          </Box>
        </Card.Body>
      </Card.Root>
      <DeviceDeleteDialog
        isOpen={state.deviceToDelete !== null}
        onClose={() => state.setDeviceToDelete(null)}
        stationLabel={state.deviceToDelete?.station_label ?? ''}
        onConfirm={state.handleDeleteConfirm}
        isDeleting={state.isDeleting}
      />
      <DeviceCloneDialog
        isOpen={state.deviceToClone !== null}
        onClose={() => state.setDeviceToClone(null)}
        sourceDevice={state.deviceToClone}
        pbxId={pbxId}
        onSuccess={state.handleCloneSuccess}
        onError={state.handleCloneError}
      />
      <DeviceMassEditDialog
        isOpen={state.massEditOpen}
        onClose={() => state.setMassEditOpen(false)}
        selectedDevices={state.devices.filter((d) =>
          state.selectedIds.has(d.id),
        )}
        onConfirm={state.handleMassEditConfirm}
        isSubmitting={state.isMassEditing}
      />
      <DeviceSetDesiredFirmwareDialog
        isOpen={state.setFirmwareDialogOpen}
        onClose={() => state.setSetFirmwareDialogOpen(false)}
        selectedCount={state.selectedIds.size}
        onConfirm={state.handleSetDesiredFirmwareConfirm}
        isSubmitting={state.isSettingFirmware}
      />
      <ConfirmDialog
        open={rebuildAllConfirmOpen}
        onClose={() => setRebuildAllConfirmOpen(false)}
        title={text(
          'devices.list.rebuildAllConfirmTitle',
          'Rebuild all device configs?',
        )}
        description={text(
          'devices.list.rebuildAllConfirmDescription',
          'You are about to rebuild the configuration for every device on this PBX. Are you sure you want to continue?',
        )}
        confirmLabel={text(
          'devices.list.rebuildAllConfirmAction',
          'Rebuild all',
        )}
        cancelLabel={text('devices.common.cancel', 'Cancel')}
        onConfirm={() =>
          state.handleRebuildAll({
            onSettled: () => setRebuildAllConfirmOpen(false),
          })
        }
        isLoading={state.isRebuilding}
      />
      <ConfirmDialog
        open={rebuildAndRebootConfirmOpen}
        onClose={() => setRebuildAndRebootConfirmOpen(false)}
        title={text(
          'devices.list.rebuildAndRebootConfirmTitle',
          'Rebuild and reboot all devices?',
        )}
        description={text(
          'devices.list.rebuildAndRebootConfirmDescription',
          'You are about to rebuild the configuration for every device on this PBX and reboot them. Are you sure you want to continue?',
        )}
        confirmLabel={text(
          'devices.list.rebuildAndRebootConfirmAction',
          'Rebuild & reboot',
        )}
        cancelLabel={text('devices.common.cancel', 'Cancel')}
        variant="destructive"
        onConfirm={() =>
          state.handleRebuildAndReboot({
            onSettled: () => setRebuildAndRebootConfirmOpen(false),
          })
        }
        isLoading={state.isRebuildingAndRebooting}
      />
      <DevicesListFiltersSideSheet
        isOpen={
          (cs === 'data' || cs === 'loading' || cs === 'empty') &&
          state.filtersVisible
        }
        value={draftFilters}
        onChange={setDraftFilters}
        onClear={() => {
          setDraftFilters({})
          state.setFilters({})
          state.setCurrentPage(1)
          state.setFiltersVisible(false)
        }}
        onCancel={() => state.setFiltersVisible(false)}
        onApply={() => {
          state.setFilters(draftFilters)
          state.setCurrentPage(1)
          state.setFiltersVisible(false)
        }}
      />
    </Box>
  )

  return Wrapper ? <Wrapper>{content}</Wrapper> : content
}
